import {
	ActionGroup,
	Alert,
	Button,
	Form,
	FormGroup,
	Spinner,
	TextInput,
} from '@patternfly/react-core';
import CenteredPage from '../../../layout/CenteredPage';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosConfig, { IZIApiError } from '../../../api/axiosConfig';
import { User } from '../../../api/security/User';
import { getCSRFToken } from '../../../api/security/CSRFToken';
import { faCheck, faEye, faEyeSlash, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';

export default function PasswordReset() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const passwordRef = useRef<HTMLInputElement>(null);
	const confirmPasswordRef = useRef<HTMLInputElement>(null);

	const [meter, setMeter] = useState(false);

	const [icon, setIcon] = useState({
		password: faEyeSlash,
		confirmPassword: faEyeSlash,
	});

	const [input, setInput] = useState({
		password: '',
		confirmPassword: '',
	});

	const [loading, setLoading] = useState<boolean>(false);
	const [notice, setNotice] = useState<React.ReactElement | null>(null);
	const [validRequest, setValidRequest] = useState<boolean>(true);

	const [error, setError] = useState({
		password: 'Please Enter New Password',
		confirmPassword: 'Please Enter Confirm Password',
	});

	const [type, setType] = useState({
		password: 'password',
		confirmPassword: 'password',
	});

	// OWASP recommended rules
	const regexMinLength = /^.{8,}$/;
	const regexLength = /^.{8,16}$/;
	const regexUppercase = /[A-Z]/;
	const regexLowercase = /[a-z]/;
	const regexNumeric = /[0-9]/;
	const regexSpecial = /[ !"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/;

	const onInputChange = (value: string, event: React.FormEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		const { name } = target;

		setInput((prev) => ({
			...prev,
			[name]: value,
		}));

		validateInput(value, target);
	};

	const validateInput = (value: string, target: HTMLInputElement) => {
		const { name } = target;

		setError((prev) => {
			const stateObj = { ...prev, [name]: '' };

			switch (name) {
				case 'password':
					if (!value) {
						stateObj[name] = 'Please Enter New Password';
					} else if (input.confirmPassword && value !== input.confirmPassword) {
						stateObj['confirmPassword'] =
							'Password and Confirm Password does not match';
					} else {
						stateObj['confirmPassword'] = input.confirmPassword
							? ''
							: error.confirmPassword;
					}
					break;

				case 'confirmPassword':
					if (!value) {
						stateObj[name] = 'Please Enter Confirm Password';
					} else if (input.password && value !== input.password) {
						stateObj[name] = 'Password and Confirm Password does not match';
					}
					break;

				default:
					break;
			}

			return stateObj;
		});
	};

	const toggleType = (event: React.MouseEvent<HTMLSpanElement>) => {
		const id = event.currentTarget.id as 'password' | 'confirmPassword';

		setType((prev) => ({
			...prev,
			[id]: prev[id] === 'password' ? 'text' : 'password',
		}));

		setIcon((prev) => ({
			...prev,
			[id]: prev[id] === faEyeSlash ? faEye : faEyeSlash,
		}));
	};

	// Strength Tracking
	const passwordTracker = {
		minLength: regexMinLength.test(input.password),
		maxLength: regexLength.test(input.password),
		uppercase: regexUppercase.test(input.password),
		lowercase: regexLowercase.test(input.password),
		number: regexNumeric.test(input.password),
		specialChar: regexSpecial.test(input.password),
	};

	const passwordStrength = Object.values(passwordTracker).filter(Boolean).length;

	const getStrengthColor = (score: number) => {
		switch (score) {
			case 1:
				return '#ff4d4d';
			case 2:
				return '#ff8533';
			case 3:
				return '#ffcc00';
			case 4:
				return '#99cc00';
			case 5:
				return 'green';
			default:
				return '#f5f5f5';
		}
	};

	const getStrengthText = (score: number) => {
		switch (score) {
			case 1:
				return 'Weak';
			case 2:
				return 'Fair';
			case 3:
				return 'OK';
			case 4:
				return 'Good';
			case 5:
				return 'Strong';
			default:
				return '';
		}
	};

	useEffect(() => {
		getCSRFToken()
			.then((token) => {
				axiosConfig.instance.defaults.headers['CSRF-TOKEN'] = token;
			})
			.catch(console.error);
	}, []);

	// Validate URL params
	useEffect(() => {
		if (
			!searchParams.get('validation_key') ||
			!searchParams.get('user_id') ||
			searchParams.get('validation_key') === '' ||
			searchParams.get('user_id') === ''
		) {
			setValidRequest(false);

			setNotice(
				<Alert
					variant="danger"
					isInline
					isPlain
					title="The request is invalid. Please try again or contact your system administrator."
					data-testid="passwordreset-invalidrequest"
				/>
			);
		}
	}, [searchParams]);

	const resetPassword = () => {
		if (loading) return;

		const password = passwordRef.current?.value;
		const user_id = searchParams.get('user_id');
		const validation = searchParams.get('validation_key');

		if (!user_id || !validation || !password) return;

		setNotice(null);
		setLoading(true);

		const userId = parseInt(user_id);

		User.ResetPassword(userId, password, validation, false)
			.then(() => {
				setNotice(
					<Alert
						variant="success"
						isInline
						isPlain
						title="Successfully updated password. Redirecting to login..."
						data-testid="passwordreset-success"
					/>
				);

				setValidRequest(false);

				setTimeout(() => {
					navigate('/login');
				}, 3000);
			})
			.catch((err: unknown) => {
				const axiosErr: AxiosError = err as AxiosError;
				setNotice(
					<Alert
						variant="danger"
						isInline
						isPlain
						title={
							'Something went wrong resetting your password, ' +
							((axiosErr.response?.data as IZIApiError).message ??
								'please contact the administrator')
						}
						data-testid="passwordreset-error"
					/>
				);

				setLoading(false);
			});
	};

	if (!validRequest) {
		return (
			<CenteredPage
				description="The request is invalid."
				notice={notice}
			>
				<Button
					onClick={() => navigate('/login')}
					variant="primary"
					data-testid="passwordreset-login"
				>
					Return to Login
				</Button>
			</CenteredPage>
		);
	}

	return (
		<CenteredPage
			description="Please enter your new password:"
			notice={notice}
		>
			<Form>
				{/* Password */}
				<FormGroup
					label="New Password:"
					isRequired
					fieldId="newPassword"
					helperTextInvalid={error.password}
					helperTextInvalidIcon={<>*</>}
					validated={error.password ? 'error' : 'default'}
				>
					<TextInput
						isRequired
						isDisabled={!validRequest}
						onFocus={() => setMeter(true)}
						onChange={onInputChange}
						type={
							type.password as
								| 'number'
								| 'password'
								| 'text'
								| 'search'
								| 'time'
								| 'url'
								| 'date'
								| 'datetime-local'
								| 'email'
								| 'month'
								| 'tel'
								| undefined
						}
						name="password"
						id="password"
						aria-label="New Password"
						data-testid="passwordreset-password"
						autoComplete="new-password"
						ref={passwordRef}
					/>
					<span className="p-validate">
						{error.password === '' ? (
							<FontAwesomeIcon
								icon={faCheck}
								className="fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								className="fa-x"
							/>
						)}
					</span>
					<span
						id="password"
						className="p-viewer"
						onClick={toggleType}
					>
						<FontAwesomeIcon icon={icon.password} />
					</span>
				</FormGroup>

				{/* Confirm Password */}
				<FormGroup
					label="Confirm New Password:"
					isRequired
					fieldId="confirmPassword"
					helperTextInvalid={error.confirmPassword}
					helperTextInvalidIcon={<>*</>}
					validated={error.confirmPassword ? 'error' : 'default'}
				>
					<TextInput
						isRequired
						isDisabled={!validRequest}
						onChange={onInputChange}
						type={
							type.confirmPassword as
								| 'number'
								| 'password'
								| 'text'
								| 'search'
								| 'time'
								| 'url'
								| 'date'
								| 'datetime-local'
								| 'email'
								| 'month'
								| 'tel'
								| undefined
						}
						name="confirmPassword"
						id="confirm-password"
						aria-label="Confirm New Password"
						data-testid="passwordreset-confirmpassword"
						autoComplete="new-password"
						ref={confirmPasswordRef}
					/>
					<span className="p-validate">
						{error.confirmPassword === '' ? (
							<FontAwesomeIcon
								icon={faCheck}
								className="fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								className="fa-x"
							/>
						)}
					</span>
					<span
						id="confirmPassword"
						className="p-viewer"
						onClick={toggleType}
					>
						<FontAwesomeIcon icon={icon.confirmPassword} />
					</span>
				</FormGroup>

				{/* Password Strength Meter */}
				{meter && (
					<>
						<span>Password Strength Indicator</span>
						<div
							className="password-strength"
							style={{
								borderColor: getStrengthColor(passwordStrength),
							}}
						>
							<div
								className="password-strength-meter"
								style={{
									width: `${(passwordStrength / 5) * 100}%`,
									backgroundColor: getStrengthColor(passwordStrength),
								}}
							>
								<p>{getStrengthText(passwordStrength)}</p>
							</div>
						</div>
					</>
				)}

				{/* Requirements List */}
				<div style={{ minWidth: '500px' }}>
					<p>
						{passwordTracker.minLength ? (
							<FontAwesomeIcon
								icon={faCheck}
								color="green"
								className="fontAwesomePadding fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								color="red"
								className="fontAwesomePadding fa-x"
							/>
						)}
						Password MUST be at least 8 characters
					</p>
					<p>
						{passwordTracker.maxLength ? (
							<FontAwesomeIcon
								icon={faCheck}
								color="green"
								className="fontAwesomePadding fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								color="red"
								className="fontAwesomePadding fa-x"
							/>
						)}
						Password MUST not exceed 16 characters
					</p>
					<p>
						{passwordTracker.uppercase ? (
							<FontAwesomeIcon
								icon={faCheck}
								color="green"
								className="fontAwesomePadding fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								color="red"
								className="fontAwesomePadding fa-x"
							/>
						)}
						Password MUST include at least one uppercase letter
					</p>
					<p>
						{passwordTracker.lowercase ? (
							<FontAwesomeIcon
								icon={faCheck}
								color="green"
								className="fontAwesomePadding fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								color="red"
								className="fontAwesomePadding fa-x"
							/>
						)}
						Password MUST include at least one lowercase letter
					</p>
					<p>
						{passwordTracker.number ? (
							<FontAwesomeIcon
								icon={faCheck}
								color="green"
								className="fontAwesomePadding fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								color="red"
								className="fontAwesomePadding fa-x"
							/>
						)}
						Password MUST include at least one number
					</p>
					<p>
						{passwordTracker.specialChar ? (
							<FontAwesomeIcon
								icon={faCheck}
								color="green"
								className="fontAwesomePadding fa-check"
							/>
						) : (
							<FontAwesomeIcon
								icon={faXmark}
								color="red"
								className="fontAwesomePadding fa-x"
							/>
						)}
						Password MUST include at least one special character
					</p>
				</div>

				<ActionGroup>
					{loading ? (
						<Spinner size={'lg'} />
					) : (
						<Button
							onClick={resetPassword}
							variant="primary"
							isDisabled={
								!validRequest ||
								!passwordTracker.minLength ||
								input.password !== input.confirmPassword
							}
							data-testid="passwordreset-submit"
						>
							Reset Password
						</Button>
					)}
				</ActionGroup>
			</Form>
		</CenteredPage>
	);
}
